import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  useGetFieldAdminStockBalanceQuery,
  useGetFieldAdminStockCloseOutQuery,
  useSaveFieldAdminStockCloseOutMutation,
} from "./fieldAdminApi";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FONT_FAMILY, OK_GREEN_TEXT, WARN_YELLOW, BAD_RED_TEXT, teamLabel, installerLabel, FA_BOTTOM_NAV_HEIGHT, parseLocalDate, formatShortDay, stripErrorPrefix } from "./designTokens";
import { InstallerAvatar, useTeamRoster } from "./HS/InstallerRoster";
import { FAFieldRow, FAFormTextarea } from "./StockFormShell";
import DesignSelect from "./DesignSelect";
import { FACheckbox, useConfirm, FAGhostButton, FAPrimaryButton } from "./FAControls";
import { FAPageState } from "./FAStates";
import { FAPageShell, FAPageHeader, FANotesField, FAEmptyState, FAUnlinkedNote } from "./FALayout";

// .bs-cell — a bordered box, not bare text. The closing term takes .bs-cell.closing, which
// tints the box amber: it is the figure being signed off, and it should be findable at a
// glance among four others that merely explain it.
function ReconCell({ value }) {
  return (
    <Typography sx={{
      display: { xs: "none", sm: "block" }, fontSize: "0.88rem", fontVariantNumeric: "tabular-nums",
      color: value ? "#fff" : "rgba(255,255,255,0.45)", fontWeight: value ? 600 : 500,
    }}>
      {value}
    </Typography>
  );
}

function BalanceCell({ label, value, sub, highlight }) {
  return (
    <Box sx={{
      flex: "0 1 auto", minWidth: "104px",
      backgroundColor: highlight ? "rgba(253,185,64,0.1)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${highlight ? "rgba(253,185,64,0.4)" : "rgba(255,255,255,0.05)"}`,
      borderRadius: "10px", p: "12px 14px",
    }}>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", mb: "4px", color: highlight ? AMBER : "rgba(255,255,255,0.45)" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.55rem", fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: highlight ? AMBER : "#fff" }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: "4px" }}>{sub}</Typography>
    </Box>
  );
}

function Operator({ children }) {
  return (
    <Box component="span" sx={{
      alignSelf: "center", fontSize: "1.4rem", color: "rgba(255,255,255,0.4)",
      fontWeight: 600, textAlign: "center", flexShrink: 0,
    }}>
      {children}
    </Box>
  );
}

// .recon-head / .recon-row share this grid, so the header can never drift from the body.
const RECON_COLUMNS = "minmax(0,1.4fr) 0.7fr 0.9fr 0.7fr 0.9fr";

export default function StockCloseOut() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ask, confirmDialog } = useConfirm();
  const { nameById } = useTeamRoster();

  const { data: balance, isSuccess: isBalanceSuccess, isError: isBalanceError, error: balanceError, refetch: refetchBalance } = useGetFieldAdminStockBalanceQuery();
  const { data: closeOut, isSuccess: isCloseOutSuccess, isError: isCloseOutError, error: closeOutError, refetch: refetchCloseOut } = useGetFieldAdminStockCloseOutQuery();
  const [saveCloseOut, { isLoading: isSaving }] = useSaveFieldAdminStockCloseOutMutation();

  const [reason, setReason] = useState("");
  const [discOpen, setDiscOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [notes, setNotes] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!isCloseOutSuccess || hydrated) return;
    setHydrated(true);
    setReason(closeOut.discrepancy_reason || "");
    setResolutionNotes(closeOut.discrepancy_resolution_notes || "");
    setNotes(closeOut.notes || "");
  }, [isCloseOutSuccess, closeOut, hydrated]);

  // The gate waits on both queries, so it has to report on both. Reporting only the
  // balance query meant a failing close-out call left this page on a shimmer forever,
  // with no message and no retry.
  if (!isBalanceSuccess || !isCloseOutSuccess) {
    return (
      <FAPageState
        isError={isBalanceError || isCloseOutError}
        error={balanceError || closeOutError}
        onRetry={isCloseOutError ? refetchCloseOut : refetchBalance}
        count={5}
        tiles={0}
      />
    );
  }

  const isClosed = closeOut.closed;
  const perInstaller = balance.per_installer;

  // Who still holds devices that are neither fitted nor returned. `remaining` is counted
  // device by device on the server and can't go negative (NEU-1090), so the screen names
  // who and shows the sum rather than asking the field admin to spot it.
  const offenders = perInstaller.filter((row) => row.remaining > 0);
  const worst = offenders.length
    ? offenders.reduce((a, b) => (b.remaining > a.remaining ? b : a))
    : null;
  const unaccounted = offenders.reduce((sum, row) => sum + row.remaining, 0);

  const discrepancyTitle = !offenders.length
    ? "No discrepancy"
    : `${unaccounted} device${unaccounted === 1 ? "" : "s"} unaccounted for`
      + (offenders.length === 1 ? ` · ${installerLabel(nameById.get(worst.installer_id), worst.installer_id)}` : "");

  const discrepancySub = !offenders.length
    ? "The van count balances. You can close the day without recording a reason."
    : offenders.length === 1
      ? `${worst.remaining} device${worst.remaining === 1 ? "" : "s"} issued and not yet fitted or returned. Resolve before close-out.`
      : `${offenders.length} installers still hold devices. Resolve before close-out.`;
  const outstanding = perInstaller.reduce((sum, row) => sum + row.remaining, 0);

  const persist = async (patch, successMessage) => {
    setSaveError(null);
    const result = await saveCloseOut({
      discrepancy_reason: reason || null,
      discrepancy_resolution_notes: resolutionNotes,
      notes,
      ...patch,
    });
    if (result.error) {
      setSaveError(stripErrorPrefix(result.error?.data?.error_message) || "Could not save. Try again.");
      return false;
    }
    if (successMessage) dispatch(showSuccess(successMessage));
    return true;
  };

  const handleResolve = async () => {
    if (!reason) {
      await ask({
        title: "Choose a reason first",
        body: "Select a reason for the discrepancy before marking it resolved.",
        confirmLabel: "OK",
        cancelLabel: "Dismiss",
      });
      return;
    }
    const proceed = await ask({
      title: "Mark discrepancy resolved?",
      body: "Records the reason and resolution notes against the unaccounted devices and clears the close-out blocker.",
      confirmLabel: "Mark resolved",
    });
    if (!proceed) return;
    await persist({ discrepancy_resolved: true }, "Discrepancy resolved");
  };

  const handleSignOff = async () => {
    if (reason && !closeOut.discrepancy_resolved) {
      await ask({
        title: "Resolve the discrepancy first",
        body: "A reason has been recorded but not resolved. Mark the discrepancy resolved before closing the day’s stock balance.",
        confirmLabel: "OK",
        cancelLabel: "Dismiss",
      });
      return;
    }
    const proceed = await ask({
      title: "Confirm end-of-day close?",
      body: "Signs off the end-of-day stock balance and closes today’s record. It cannot be edited after close.",
      confirmLabel: "Confirm & close",
    });
    if (!proceed) return;
    const ok = await persist({ closed: true }, "Stock balance closed");
    if (ok) navigate("/field-admin/stock/");
  };

  return (
    <>
      <FAPageShell footer>
        <FAPageHeader
          title="End-of-Day Stock Balance"
          sub={<><Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{teamLabel(balance.team_name, balance.team_id)}</Box>{" · reconcile and sign off today’s van stock"}</>}
        />

        <FAUnlinkedNote count={balance.unlinked_installers} />

        {isClosed ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", backgroundColor: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.22)", borderRadius: "8px", p: "10px 14px", mb: "18px" }}>
            <LockOutlinedIcon sx={{ color: OK_GREEN_TEXT, fontSize: "14px", flexShrink: 0 }} />
            <Typography sx={{ color: OK_GREEN_TEXT, fontSize: "0.78rem" }}>
              Closed and signed off. Today&rsquo;s balance is a final record and can no longer be edited.
            </Typography>
          </Box>
        ) : null}

        {/* .balance-summary — an amber-tinted card of boxed terms, not a plain row of
            figures. This is the screen's headline: the number being signed off. */}
        <Box sx={{
          background: "linear-gradient(135deg, rgba(253,185,64,0.08), rgba(253,185,64,0.02))",
          border: "1px solid rgba(253,185,64,0.25)", borderRadius: "14px", p: "22px 24px", mb: "24px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", mb: "18px", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: AMBER, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Today's reconciliation
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", fontVariantNumeric: "tabular-nums" }}>
              {formatShortDay(parseLocalDate(closeOut.date))}
              {balance.team_name ? ` · ${teamLabel(balance.team_name, balance.team_id)}` : ""}
            </Typography>
          </Box>

          {/* The design draws five terms as Open − Issued − Installed − Returned = Closing.
              That does not hold here: issuing already takes a device off the van, so
              subtracting installs would count the same device twice, and a return is a
              flagged set-aside that deliberately does not move the van balance. The terms
              that do reconcile are Open + Collected − Issued = On van, with Open the van at
              the start of the day (NEU-1090), so those carry the operators and Installed and
              Returned are shown as context. */}
          <Box sx={{ display: "flex", alignItems: "stretch", gap: "14px", flexWrap: "wrap" }}>
            <BalanceCell label="Open" value={balance.opening} sub="on van at start" />
            <Operator>+</Operator>
            <BalanceCell label="Collected" value={balance.collected} sub="today" />
            <Operator>−</Operator>
            <BalanceCell label="Issued" value={balance.issued} sub="to installers" />
            <Operator>=</Operator>
            <BalanceCell label="On van" value={balance.van_expected} sub="should be on van" highlight />
            <Box sx={{ width: "1px", backgroundColor: "rgba(255,255,255,0.08)", mx: "4px", alignSelf: "stretch" }} />
            <BalanceCell label="Installed" value={balance.installed} sub="COC'd by QE" />
            <BalanceCell label="Returned" value={balance.returned} sub="flagged, set aside" />
          </Box>

          {/* .bs-foot — checks the sum against the van counted device by device. They part
              only when the scans don't add up (a device collected twice, a van device marked
              returned). It used to restate Open as Issued + On van, which always matched. */}
          <Box sx={{
            mt: "16px", pt: "16px", borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
            flexWrap: "wrap", fontSize: "0.82rem", color: "rgba(255,255,255,0.6)",
          }}>
            <Box component="span">
              {"On van by device scan "}<Box component="strong" sx={{ color: "#fff", fontWeight: 600 }}>{balance.on_van}</Box>
              {balance.van_difference ? (
                <Box component="span" sx={{ color: WARN_YELLOW, fontWeight: 600 }}>
                  {` · ${Math.abs(balance.van_difference)} ${balance.van_difference < 0 ? "fewer" : "more"} than the sum`}
                </Box>
              ) : " · matches the sum"}
            </Box>
            {balance.still_out ? (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: WARN_YELLOW, fontWeight: 600 }}>
                <WarningAmberIcon sx={{ fontSize: "0.95rem" }} />
                {`${balance.still_out} device${balance.still_out === 1 ? "" : "s"} still out with installers`}
              </Box>
            ) : (
              <Box component="span" sx={{ color: OK_GREEN_TEXT, fontWeight: 600 }}>Everything issued is accounted for</Box>
            )}
          </Box>
        </Box>

        {/* The one place this screen departs from the design's arithmetic, stated rather
            than left for the reader to notice. */}
        <Box sx={{ backgroundColor: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}`, borderRadius: "10px", p: "12px 14px", mb: "22px" }}>
          <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
            Issuing a device already takes it off the van, so subtracting its install would count it twice, and a returned
            device is set aside rather than reloaded. The terms that reconcile are{" "}
            <Box component="span" sx={{ color: "rgba(255,255,255,0.75)" }}>Open + Collected − Issued = On van</Box>; installed and
            returned are shown alongside as context. A device that is physically missing still counts as on the van until
            you record it, so the reason below is yours to give.
          </Typography>
        </Box>

        {/* Per-installer reconciliation */}
        <Box sx={{ pt: "6px", pb: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Per-installer reconciliation
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>
            {outstanding} still out
          </Typography>
        </Box>

        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden", mb: "22px" }}>
          <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: RECON_COLUMNS, gap: "10px", px: "16px", py: "11px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
            {["Installer", "Issued", "Installed", "Returned", "Still holding"].map((h) => (
              <Typography key={h} sx={{ fontSize: "0.64rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "rgba(255,255,255,0.4)" }}>
                {h}
              </Typography>
            ))}
          </Box>

          {perInstaller.length === 0 ? (
            <FAEmptyState py="60px">Nothing issued today — nothing to reconcile.</FAEmptyState>
          ) : (
            perInstaller.map((row, index) => (
              <Box
                key={row.installer_id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr auto", sm: RECON_COLUMNS },
                  gap: "10px",
                  alignItems: "center",
                  px: "16px",
                  py: "12px",
                  borderBottom: index === perInstaller.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                  // .recon-row.warn — a row that does not balance is tinted and rules its
                  // left edge, so it is findable without reading every number.
                  ...(row.remaining > 0 ? {
                    backgroundColor: "rgba(251,191,36,0.05)",
                    borderLeft: `2px solid ${WARN_YELLOW}`,
                    pl: "14px",
                  } : null),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <InstallerAvatar name={nameById.get(row.installer_id)} id={row.installer_id} />
                  <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {installerLabel(nameById.get(row.installer_id), row.installer_id)}
                  </Typography>
                </Box>
                {/* .recon-cell / .recon-cell.muted — a zero is dimmed, so the eye lands on
                    the rows that actually moved stock. */}
                <ReconCell value={row.issued} />
                <ReconCell value={row.installed} />
                <ReconCell value={row.returned} />
                {/* .recon-net — always the accent, warn-toned when it does not balance. */}
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, textAlign: { xs: "right", sm: "left" }, fontVariantNumeric: "tabular-nums", color: row.remaining > 0 ? WARN_YELLOW : AMBER }}>
                  {row.remaining}
                </Typography>
              </Box>
            ))
          )}

          {/* .recon-totals — the column sums, so the per-person rows can be checked against
              the card above without adding them up by hand. */}
          {perInstaller.length ? (
            <Box sx={{
              display: { xs: "none", sm: "grid" }, gridTemplateColumns: RECON_COLUMNS, gap: "10px",
              px: "16px", py: "12px", alignItems: "center", fontWeight: 700,
              backgroundColor: "rgba(255,255,255,0.025)", borderTop: "1px solid rgba(255,255,255,0.04)",
            }}>
              <Typography sx={{ fontSize: "0.88rem", color: "#fff", fontWeight: 700 }}>Totals</Typography>
              {["issued", "installed", "returned"].map((key) => (
                <Typography key={key} sx={{ fontSize: "0.88rem", color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {perInstaller.reduce((sum, row) => sum + (row[key] || 0), 0)}
                </Typography>
              ))}
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: AMBER }}>
                {perInstaller.reduce((sum, row) => sum + (row.remaining || 0), 0)}
              </Typography>
            </Box>
          ) : null}
        </Box>

        {/* .discrepancy-card — the design leads with the problem it found, named and worked
            out, and keeps the form collapsed behind "Add reason". The old card asked "if the
            count doesn't match, record why" and left the field admin to find the mismatch
            themselves — the numbers to spot it are already on the screen above. */}
        <Box sx={{
          mt: "14px", mb: "22px", borderRadius: "12px", p: "14px 16px",
          backgroundColor: offenders.length ? "rgba(251,191,36,0.06)" : "rgba(255,255,255,0.02)",
          border: `1px solid ${offenders.length ? "rgba(251,191,36,0.28)" : BORDER}`,
          opacity: closeOut.discrepancy_resolved ? 0.7 : 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Box sx={{
              width: "30px", height: "30px", borderRadius: "8px", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              backgroundColor: offenders.length ? "rgba(251,191,36,0.16)" : "rgba(255,255,255,0.05)",
              color: offenders.length ? WARN_YELLOW : "rgba(255,255,255,0.45)",
            }}>
              {closeOut.discrepancy_resolved ? <CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} /> : <WarningAmberIcon sx={{ fontSize: "1rem" }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: "200px" }}>
              <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: offenders.length ? WARN_YELLOW : "rgba(255,255,255,0.75)" }}>
                {closeOut.discrepancy_resolved
                  ? "Discrepancy resolved"
                  : discrepancyTitle}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", mt: "2px", lineHeight: 1.4 }}>
                {closeOut.discrepancy_resolved
                  ? `Recorded as “${reason}”. The day can be closed.`
                  : discrepancySub}
              </Typography>
            </Box>

            {!isClosed && !closeOut.discrepancy_resolved ? (
              <Box
                component="button"
                type="button"
                onClick={() => setDiscOpen((o) => !o)}
                sx={{
                  ml: "auto", flexShrink: 0, px: "10px", py: "5px", borderRadius: "6px",
                  cursor: "pointer", fontFamily: FONT_FAMILY, fontSize: "0.74rem", fontWeight: 600,
                  background: "transparent",
                  border: `1px solid ${offenders.length ? "rgba(251,191,36,0.4)" : "rgba(255,255,255,0.14)"}`,
                  color: offenders.length ? WARN_YELLOW : "rgba(255,255,255,0.6)",
                  "&:hover": { backgroundColor: offenders.length ? "rgba(251,191,36,0.1)" : "rgba(255,255,255,0.04)" },
                }}
              >
                {discOpen ? "Collapse" : "Add reason"}
              </Box>
            ) : null}
          </Box>

          {/* .disc-form — collapsed until asked for, so a balanced day is one line. */}
          {discOpen && !isClosed && !closeOut.discrepancy_resolved ? (
            <Box sx={{ mt: "12px", pt: "12px", borderTop: `1px solid ${offenders.length ? "rgba(251,191,36,0.2)" : BORDER}` }}>
              <FAFieldRow label="Reason for discrepancy">
                <DesignSelect
                  id="disc-reason"
                  value={reason}
                  onChange={setReason}
                  placeholder="— Choose a reason —"
                  options={closeOut.discrepancy_reasons.map((r) => ({ value: r, label: r }))}
                />
              </FAFieldRow>

              <Box sx={{ mt: "10px" }}>
                <FAFieldRow label="Resolution notes">
                  <FAFormTextarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="e.g. Confirmed with the installer — unit left at site for QE pickup tomorrow. Not lost, will reconcile on the next close-out."
                    sx={{ minHeight: "64px", fontSize: "0.86rem" }}
                  />
                </FAFieldRow>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "12px" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleResolve}
                  sx={{
                    px: "14px", py: "9px", borderRadius: "9px", cursor: "pointer",
                    backgroundColor: "rgba(253,185,64,0.1)", border: "1px solid rgba(253,185,64,0.3)",
                    color: AMBER, fontWeight: 600, fontSize: "0.82rem", fontFamily: FONT_FAMILY,
                    "&:hover": { backgroundColor: "rgba(253,185,64,0.18)" },
                  }}
                >
                  Mark resolved
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>

        {/* Close-out notes */}
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", px: "16px", py: "14px", mb: "10px" }}>
          <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, color: "rgba(255,255,255,0.65)", mb: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
            Close-out notes
            <Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Optional
            </Box>
          </Typography>
          <FANotesField
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Two devices held back for tomorrow's Mofolo run — agreed with the PM."
          minHeight="80px"
        />
        </Box>

        {saveError ? (
          <Typography sx={{ color: BAD_RED_TEXT, fontSize: "0.82rem", mb: "8px" }}>{saveError}</Typography>
        ) : null}
      </FAPageShell>

      {/* Sticky sign-off, above the bottom nav — same stacking as the H&S step footer. */}
      {isClosed ? null : (
        <Box
          sx={{
            position: "fixed", left: 0, right: 0, bottom: FA_BOTTOM_NAV_HEIGHT,
            backgroundColor: "rgba(13,17,23,0.94)", backdropFilter: "blur(12px)",
            borderTop: "1px solid rgba(255,255,255,0.06)", pt: "12px", pb: "14px", zIndex: 79,
          }}
        >
          <Box sx={{ px: { xs: "16px", sm: "32px" }, maxWidth: "860px", mx: "auto", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "200px" }}>
              <FACheckbox
                compact
                checked={acknowledged}
                onChange={() => setAcknowledged((v) => !v)}
                label="I confirm this is the end-of-day stock balance"
                labelSx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.75)" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <FAGhostButton onClick={() => navigate("/field-admin/stock/")} sx={{ py: "11px" }}>
                Cancel
              </FAGhostButton>
              <FAPrimaryButton onClick={handleSignOff} disabled={!acknowledged || isSaving} sx={{ px: "18px", py: "11px" }}>
                {isSaving ? "Closing…" : "Confirm & close"}
              </FAPrimaryButton>
            </Box>
          </Box>
        </Box>
      )}

      {confirmDialog}
    </>
  );
}
